const PRODUCTION_API_URL = "https://api.collaboratorx.ai";
const LOCAL_API_URL = "http://localhost:8030";

function isLocalBrowserHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/** Resolve API base URL from browser hostname so tunnel domains never call localhost. */
export function getApiUrl(): string {
  if (typeof window !== "undefined") {
    if (isLocalBrowserHost(window.location.hostname)) {
      return LOCAL_API_URL;
    }
    return PRODUCTION_API_URL;
  }
  return process.env.NEXT_PUBLIC_API_URL ?? PRODUCTION_API_URL;
}

/** @deprecated Prefer getApiUrl() — this constant is not hostname-aware. */
export const API_URL = PRODUCTION_API_URL;

const TOKEN_KEY = "hermes.token";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (typeof init.body === "string") headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${getApiUrl()}${path}`, { ...init, headers });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const payload = await response.json();
      message = payload.error ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  /** Always sends a JSON body — Fastify rejects application/json POSTs with an empty body. */
  post: <T,>(path: string, body: unknown = {}) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  patch: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T,>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", body: formData }),
};

export function fileUrl(url: string | null | undefined) {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `${getApiUrl()}${trimmed}`;
}
